<template>
  <div class="blog-page-wrapper">
    <div class="container px-3 px-md-5 py-5">
      <div class="row justify-content-center">
        <div class="col-lg-10" v-if="blog" dir="ltr">
          <!-- Featured Image -->
          <img
            class="img-fluid rounded mb-4 shadow-sm"
            :src="getImageUrl(blog.image)"
            :alt="blog.title"
            style="height: 50vh; width: 100%; object-fit: cover; object-position: center;"
          />

          <!-- Title -->
          <h1 class="mb-4 fw-bold blog-title">{{ blog.title }}</h1>

          <!-- Metadata -->
          <div class="d-flex flex-wrap text-muted mb-4 metadata">
            <small class="me-4">
              <i class="bi bi-person me-1"></i> {{ blog.author }}
            </small>
            <small class="me-4">
              <i class="bi bi-calendar me-1"></i> {{ formatDate(blog.date) }}
            </small>
            <small v-if="blog.categories && blog.categories.length">
              <i class="bi bi-tag me-1"></i> {{ blog.categories[0].name }}
            </small>
          </div>

          <!-- Blog Content (Markdown rendered) -->
          <div class="blog-body" v-html="renderMarkdown(blog.body)"></div>

          <!-- Back Button -->
          <div class="text-center mt-5">
            <router-link to="/blog" class="btn btn-secondary px-4 py-2">
              ← Back to Blog List
            </router-link>
          </div>
        </div>

        <div v-else class="text-center my-5">
          Loading blog post...
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import MarkdownIt from "markdown-it";

const md = new MarkdownIt();
const API_BASE = "https://admin.nexusfocusconsultancy.com";
const MEDIA_BASE = "https://media.nexusfocusconsultancy.com";

export default {
  name: "BlogDetailView",
  data() {
    return {
      blog: null,
      relatedPosts: [],
    };
  },
  async created() {
    try {
      const slug = this.$route.params.slug;
      const response = await axios.get(`${API_BASE}/blog/api/posts/${slug}/`);
      this.blog = response.data;

      if (this.blog.categories && this.blog.categories.length) {
        const category = this.blog.categories[0].slug || this.blog.categories[0].id;
        const relatedResponse = await axios.get(
          `${API_BASE}/blog/api/posts/?category=${category}`
        );
        // Handle paginated and non-paginated related posts data:
        this.relatedPosts = relatedResponse.data.results
          ? relatedResponse.data.results.filter(post => post.id !== this.blog.id)
          : relatedResponse.data.filter(post => post.id !== this.blog.id);
      }
    } catch (error) {
      console.error("Failed to load blog detail:", error);
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "";
      const options = { year: "numeric", month: "long", day: "numeric" };
      return new Date(dateStr).toLocaleDateString(undefined, options);
    },
    renderMarkdown(text) {
      if (!text) return "";
      const sentences = text.match(/[^\.!\?]+[\.!\?]+(\s|$)/g) || [text];
      const paragraphs = [];
      for (let i = 0; i < sentences.length; i += 5) {
        paragraphs.push(sentences.slice(i, i + 5).join(" ").trim());
      }
      const modifiedText = paragraphs.join("\n\n");
      return md.render(modifiedText);
    },
    getImageUrl(imagePath) {
      if (!imagePath) return "/default-blog.jpg";
      if (imagePath.startsWith("http")) return imagePath;
      return `${MEDIA_BASE}${imagePath}`;
    },
  },
};
</script>


<style scoped>
.blog-page-wrapper {
  min-height: 100vh;
  background: linear-gradient(to left, #a8e6a3, #065f2f);
  padding: 3rem 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.container {
  max-width: 960px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
  padding: 3rem 2rem;
}

/* Blog Title */
.blog-title {
  color: #42b983;
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 900;
  margin-bottom: 1rem;
  line-height: 1.2;
}

/* Metadata */
.metadata small {
  font-size: 0.9rem;
}

/* Blog body */
.blog-body {
  font-size: clamp(1rem, 1.5vw, 1.125rem);
  line-height: 1.85;
  color: #333;
  text-align: left;
  word-wrap: break-word;
  max-width: 720px;
  margin: 0 auto;
}

/* Paragraphs */
.blog-body p {
  margin-bottom: 1.8rem;
  text-indent: 1.2em;
  letter-spacing: 0.02em;
}

/* Headings */
.blog-body h1,
.blog-body h2,
.blog-body h3,
.blog-body h4 {
  font-weight: 700;
  color: #222;
  margin-top: 3rem;
  margin-bottom: 1rem;
  line-height: 1.3;
}

.blog-body h2 {
  font-size: 1.8rem;
}

.blog-body h3 {
  font-size: 1.5rem;
}

.blog-body h4 {
  font-size: 1.3rem;
}

/* Lists */
.blog-body ul,
.blog-body ol {
  padding-left: 1.8rem;
  margin-bottom: 1.8rem;
}

/* Blockquotes */
.blog-body blockquote {
  border-left: 5px solid #42b983;
  padding-left: 1rem;
  color: #555;
  font-style: italic;
  margin: 2rem 0;
  background: #f0f9f7;
  border-radius: 6px;
}

/* Images */
.blog-body img {
  max-width: 100%;
  height: auto;
  margin: 2rem 0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(66, 185, 131, 0.2);
}

/* Links */
.blog-body a {
  color: #42b983;
  text-decoration: underline;
  transition: color 0.3s ease;
}
.blog-body a:hover {
  color: #36996a;
}

/* Back button */
.btn-secondary {
  background-color: #42b983;
  border: none;
  color: white;
  font-weight: 600;
  transition: background-color 0.3s ease;
}
.btn-secondary:hover {
  background-color: #36996a;
  color: white;
}

/* Responsive tweaks */
@media (max-width: 576px) {
  .blog-body {
    font-size: 1rem;
    line-height: 1.6;
  }

  .blog-body h2 {
    font-size: 1.4rem;
  }

  .blog-body h3 {
    font-size: 1.2rem;
  }

  .blog-body h4 {
    font-size: 1.1rem;
  }
}
</style>
